/**
 * Persist + unload helpers for Zoom multi-device lock (web).
 *
 * sessionStorage keeps participantToken across same-tab reload so join can reuse
 * the ACTIVE lock instead of calling web-acquire-zoom-lock again.
 * Keepalive fetch releases the lock on pagehide/beforeunload where axios may abort.
 */

import config from 'config';
import mvpAuth from './mvpAuth';
import { getDeviceTimezone } from './timezone';

const STORAGE_PREFIX = 'ced:mvp-zoom-participant:';

function storageKey(sessionUserId) {
  return `${STORAGE_PREFIX}${String(sessionUserId)}`;
}

export function loadPersistedParticipantToken(sessionUserId) {
  if (sessionUserId == null || String(sessionUserId).trim() === '') return '';
  try {
    const raw = sessionStorage.getItem(storageKey(sessionUserId));
    return raw != null && String(raw).trim() ? String(raw).trim() : '';
  } catch (_) {
    return '';
  }
}

export function persistParticipantToken(sessionUserId, participantToken) {
  if (sessionUserId == null || String(sessionUserId).trim() === '') return;
  if (participantToken == null || String(participantToken).trim() === '') return;
  try {
    sessionStorage.setItem(storageKey(sessionUserId), String(participantToken).trim());
  } catch (_) {
    /* private mode / quota — ignore */
  }
}

export function clearPersistedParticipantToken(sessionUserId) {
  if (sessionUserId == null || String(sessionUserId).trim() === '') return;
  try {
    sessionStorage.removeItem(storageKey(sessionUserId));
  } catch (_) {
    /* ignore */
  }
}

function resolveApiBaseUrl() {
  const api = (config && config.api) || {};
  return String(api.api || api.baseUrl || '').replace(/\/$/, '');
}

/**
 * Best-effort POST left during unload. Prefer fetch+keepalive (supports Authorization);
 * sendBeacon cannot set auth headers reliably.
 *
 * @param {string} participantToken
 * @returns {boolean} true if a network request was started
 */
export function releaseParticipantLockKeepalive(participantToken) {
  const token = participantToken != null ? String(participantToken).trim() : '';
  if (!token) return false;

  const base = resolveApiBaseUrl();
  if (!base) return false;

  const url = `${base}/mvp/web-update-session-status`;
  const authHeaders = typeof mvpAuth.getHeaderForMvp === 'function' ? mvpAuth.getHeaderForMvp() : {};
  const language =
    (typeof localStorage !== 'undefined' && localStorage.getItem('ced:language')) ||
    (typeof sessionStorage !== 'undefined' && sessionStorage.getItem('ced:session-language')) ||
    'vi';
  let timezone = 'UTC';
  try {
    timezone = getDeviceTimezone() || 'UTC';
  } catch (_) {
    /* ignore */
  }

  const headers = {
    'Content-Type': 'application/json',
    'Accept-Language': language,
    'Accept-Timezone': timezone,
  };
  if (authHeaders && authHeaders.authorization) {
    headers.authorization = authHeaders.authorization;
  }

  const body = JSON.stringify({ participantToken: token, status: 'left' });

  try {
    if (typeof fetch === 'function') {
      fetch(url, {
        method: 'POST',
        headers,
        body,
        keepalive: true,
        credentials: 'same-origin',
      }).catch(() => {});
      return true;
    }
  } catch (_) {
    /* fall through */
  }

  try {
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const blob = new Blob([body], { type: 'application/json' });
      return !!navigator.sendBeacon(url, blob);
    }
  } catch (_) {
    /* ignore */
  }

  return false;
}
